import {
  CARD_STYLE_RADIUS_PIXELS,
  CARD_STYLE_VARIABLE_PIXELS,
} from 'base/styleguide/card_styles';
import { generateConceptVariables } from 'base/styleguide/concept';
import { ShadeKey } from 'base/styleguide/types';
import {
  ConceptAPI,
  getGlobalConceptsStore,
} from 'blocks/editor/styleguide/concept_store';
import { CSSProperties } from 'react';
import { globalCardStyleOverrideStore } from '../card_styles';

export function useConceptStyleGuideVariables(concept?: ConceptAPI) {
  if (concept == null) return;

  const conceptStore = getGlobalConceptsStore();
  const height = conceptStore.logo?.height;
  const logoUrlLight = conceptStore.getRenderedLogo('light')?.value;
  const logoUrlDark = conceptStore.getRenderedLogo('dark')?.value;

  const cardStyle = globalCardStyleOverrideStore.get() ?? concept.cardStyle;
  const cardStyleRadiusOption = concept.cardStyleRadiusOption;

  return [
    ...concept.colorVariables.entries(),
    ...Object.entries(generateConceptVariables(concept)).map(
      ([name, value]) => [name, concept.colorVariables.get(value)],
    ),
    ...Object.entries({
      ...CARD_STYLE_VARIABLE_PIXELS[cardStyle],
      ...CARD_STYLE_RADIUS_PIXELS[cardStyleRadiusOption],
    }).map(([k, v]) => [k, `${v}px`]),
    // TODO: Move to Concept API
    ['logo-height', `${height ?? 32}px`],
    // TODO: Move this somewhere better and clean it up … this is a bit hacky
    ['logo-url-light', `url(${logoUrlLight?.src})`],
    ['logo-aspect-ratio-light', logoUrlLight?.aspectRatio],
    ['logo-url-dark', `url(${logoUrlDark?.src})`],
    ['logo-aspect-ratio-dark', logoUrlDark?.aspectRatio],
  ].reduce<CSSProperties>((acc, [name, value]) => {
    // @ts-ignore
    acc[`--${name}`] = value;
    // @ts-ignore
    acc[`--u-relume-variable--${name}`] = value;
    return acc;
  }, {});
}

type SchemesTemplate = Readonly<{
  header: readonly [ShadeKey, ShadeKey, ShadeKey];
  middle: readonly [
    ShadeKey,
    ShadeKey,
    ShadeKey,
    ShadeKey,
    ShadeKey,
    ShadeKey,
    ShadeKey,
  ];
  footer: ShadeKey;
}>;

const StrippedBackSchemesTemplate: SchemesTemplate = {
  header: ['neutral-shade-0', 'neutral-shade-0', 'neutral-shade-1'],
  middle: [
    'neutral-shade-1',
    'neutral-shade-0',
    'neutral-shade-0',
    'neutral-shade-1',
    'neutral-shade-1',
    'neutral-shade-0',
    'neutral-shade-1',
  ],
  footer: 'neutral-shade-0',
};

const MinimalSchemesTemplates: SchemesTemplate[] = (
  [
    'neutral-shade-0',
    'neutral-shade-1',
    'chromatic1-shade-1',
  ] satisfies ShadeKey[]
).map((headerColor) => ({
  header: ['neutral-shade-0', headerColor, 'neutral-shade-0'],
  middle: [
    'neutral-shade-0',
    'chromatic1-shade-1',
    'neutral-shade-0',
    'neutral-shade-1',
    'neutral-shade-1',
    'neutral-shade-0',
    'chromatic1-shade-1',
  ],
  footer: 'neutral-shade-0',
}));

const ProfessionalSchemesTemplates: SchemesTemplate[] = (
  [
    [
      ['neutral-shade-0', 'neutral-shade-0', 'neutral-shade-1'],
      'neutral-shade-0',
    ],
    [
      ['neutral-shade-7', 'neutral-shade-7', 'neutral-shade-6'],
      'neutral-shade-7',
    ],
    [
      ['chromatic1-shade-7', 'chromatic1-shade-7', 'chromatic1-shade-6'],
      'neutral-shade-0',
    ],
  ] as const
).map(([header, footer]) => ({
  header,
  middle: [
    'neutral-shade-0',
    'neutral-shade-0',
    'neutral-shade-2',
    'neutral-shade-0',
    'neutral-shade-0',
    'chromatic1-shade-6',
    'neutral-shade-0',
  ],
  footer,
}));

const PlayfulSchemesTemplates: SchemesTemplate[] = (
  [
    ['chromatic1-shade-2', 'chromatic1-shade-6'],
    ['chromatic2-shade-2', 'chromatic2-shade-6'],
  ] as const
).map(([header, footer]) => ({
  header: ['neutral-shade-0', header, header],
  middle: [
    'neutral-shade-0',
    'chromatic2-shade-2',
    'neutral-shade-0',
    'neutral-shade-0',
    'chromatic1-shade-2',
    'chromatic1-shade-2',
    'neutral-shade-0',
  ],
  footer,
}));

const QuirkySchemesTemplates: SchemesTemplate[] = (
  [
    [
      ['chromatic1-shade-2', 'chromatic1-shade-3', 'chromatic1-shade-1'],
      'chromatic1-shade-5',
    ],
    [
      ['chromatic2-shade-2', 'chromatic2-shade-3', 'chromatic2-shade-1'],
      'chromatic2-shade-5',
    ],
  ] as const
).map(([header, footer]) => ({
  header,
  middle: [
    'neutral-shade-0',
    'chromatic1-shade-1',
    'neutral-shade-0',
    'neutral-shade-0',
    'chromatic2-shade-1',
    'neutral-shade-0',
    'neutral-shade-0',
  ],
  footer,
}));

export function generateSchemeTemplates(
  concept: ConceptAPI,
): SchemesTemplate[] {
  return [
    StrippedBackSchemesTemplate,
    ...MinimalSchemesTemplates,
    ...ProfessionalSchemesTemplates,
    ...(concept.chromatics.length > 1 ? PlayfulSchemesTemplates : []),
    ...(concept.chromatics.length > 1 ? QuirkySchemesTemplates : []),
  ];
}
